import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { ArrowBack, Email } from '@mui/icons-material';
import { useAppNavigation } from '../hooks/useAppNavigation';
import authService from '../services/authService';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const navigate = useAppNavigation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await authService.requestPasswordReset(email);
      
      if (response.success) {
        setMessage(response.message || 'If an account with this email exists, you will receive a password reset link.');
        setSubmitted(true);
        
        // For testing purposes, if we get a reset token, show it
        if (response.data?.reset_token) {
          console.log('Reset token for testing:', response.data.reset_token);
          setMessage(
            `${response.message || 'Password reset requested successfully.'}\n\n` +
            `For testing: Reset token is ${response.data.reset_token}`
          );
        }
      } else {
        setError(response.error || 'Failed to process password reset request');
      }
    } catch (err) {
      console.error('Password reset request error:', err);
      setError('An error occurred while processing your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate.to.LOGIN();
  };

  return (
    <Container 
      component="main" 
      maxWidth="sm"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          width: '100%',
          maxWidth: 400,
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Email 
            sx={{ 
              fontSize: 48, 
              color: 'primary.main', 
              mb: 2 
            }} 
          />
          
          <Typography 
            component="h1" 
            variant="h4" 
            sx={{ 
              mb: 1,
              fontWeight: 'bold',
              textAlign: 'center'
            }}
          >
            Forgot Password?
          </Typography>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 3,
              textAlign: 'center'
            }}
          >
            {submitted 
              ? "We've sent you instructions to reset your password."
              : "Enter your email address and we'll send you a link to reset your password."
            }
          </Typography>

          {error && (
            <Alert 
              severity="error" 
              sx={{ width: '100%', mb: 2 }}
            >
              {error}
            </Alert>
          )}

          {message && (
            <Alert 
              severity="success" 
              sx={{ 
                width: '100%', 
                mb: 2,
                whiteSpace: 'pre-line'
              }}
            >
              {message}
            </Alert>
          )}

          {!submitted && (
            <Box 
              component="form" 
              onSubmit={handleSubmit} 
              sx={{ width: '100%' }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                sx={{ mb: 3 }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ 
                  mb: 2,
                  py: 1.5,
                  fontSize: '1rem'
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
            <ArrowBack sx={{ fontSize: 16, mr: 0.5 }} />
            <Link 
              component="button"
              variant="body2"
              onClick={handleBackToLogin}
              sx={{ 
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Back to Login
            </Link>
          </Box>

          {submitted && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Didn't receive an email? Check your spam folder or{' '}
                <Link 
                  component="button"
                  variant="body2"
                  onClick={() => {
                    setSubmitted(false);
                    setMessage('');
                    setError('');
                  }}
                  sx={{ 
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  try again
                </Link>
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ForgotPassword;
